import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { Patient } from '../../../types/types';
import { UseDurationScheduleReturn } from './useDurationSchedule';
import { UseFormsAndRemindersReturn } from './useFormsAndReminders';
import { UseRecurrenceReturn } from './useRecurrence';
import { useRecurrenceDataGeneration } from './useRecurrenceDataGeneration';
import {
  useCreateAppointmentMutation,
  useCreateAppointmentSeriesMutation,
  useUpdateAppointmentMutation,
  Appointment as AppointmentResponse,
  AppointmentUpdate,
  SeriesResponse,
} from '@features/appointments';
import { ExpandableFieldItem } from '@features/appointment-types';
import { formatAppointmentCreatedNotification } from '../utils/appointmentNotifications';
import {
  getModalityPlaceOfServiceError,
  getTelehealthForModality,
  normalizeCptCodes,
  type VisitModality,
} from '../../../utils/modalityPlaceOfService';
import {
  buildSessionBillingPayload,
  getSessionBillingCptCodes,
  getSessionBillingPlaceOfService,
  type SessionBillingPayload,
} from '../../../utils/sessionBilling';

type AppointmentUpdateWithSessionBilling = AppointmentUpdate & {
  session_billing?: SessionBillingPayload | null;
};

interface UseAppointmentSubmissionProps {
  mode: 'create' | 'edit';
  selectedPatient?: Patient;
  selectedAppointmentType: UseFormsAndRemindersReturn['selectedAppointmentType'];
  durationSchedule: UseDurationScheduleReturn;
  recurrence: UseRecurrenceReturn;
  // Legacy fallback: templateId comes from templatesList?.[0]?.template_id in parent components
  // This is kept for backwards compatibility during the migration from template_id + encounter_type
  // to the new encounter_type_id system. In the future, this should be removed as
  // selectedAppointmentType.template_id should always have the correct value.
  templateId?: string | null;
  selectedForms?: ExpandableFieldItem[];
  selectedReminders?: ExpandableFieldItem[];
  telehealth?: boolean;
  modality?: VisitModality;
  placeOfService?: string | null;
  cptCodes?: string[] | null;
  useJotmeet?: boolean | null;
  notes?: string;
  labelIds?: string[];
  /**
   * Whether the submitted values deviate from the selected type's defaults
   * (see appointmentOverridesTypeDefaults). Persists override_defaults=true so
   * the appointment stops following its type — the flag is never sent as
   * false, matching the other appointment-editing surfaces.
   */
  overridesTypeDefaults?: boolean;
  providerId?: string;

  // For edit mode
  appointment?: AppointmentResponse;
  onAppointmentUpdate?: (
    appointmentId: string,
    updates: AppointmentUpdate,
    seriesId?: string,
  ) => Promise<void>;

  // For create mode
  onAppointmentCreated?: (appointment: AppointmentResponse | SeriesResponse) => void;
}

export interface UseAppointmentSubmissionReturn {
  isSubmitting: boolean;
  error: string | null;
  submit: () => Promise<void>;
  setError: (error: string | null) => void;
}

/**
 * Hook to handle appointment submission for both create and edit modes
 */
export const useAppointmentSubmission = ({
  mode,
  selectedPatient,
  selectedAppointmentType,
  durationSchedule,
  recurrence,
  templateId,
  selectedForms,
  selectedReminders,
  telehealth,
  modality,
  placeOfService,
  cptCodes,
  useJotmeet,
  notes,
  labelIds = [],
  overridesTypeDefaults = false,
  providerId,
  appointment,
  onAppointmentUpdate,
  onAppointmentCreated,
}: UseAppointmentSubmissionProps): UseAppointmentSubmissionReturn => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const [createAppointment] = useCreateAppointmentMutation();
  const [createAppointmentSeries] = useCreateAppointmentSeriesMutation();
  const [updateAppointment] = useUpdateAppointmentMutation();
  const { generateRecurrenceData } = useRecurrenceDataGeneration({
    recurrence,
  });

  const submit = async (): Promise<void> => {
    try {
      const { startDateTime, duration } = durationSchedule.getAppointmentDateTime();

      if (!startDateTime || !duration) {
        setError('Invalid date or duration');
        return;
      }

      const sessionError = getModalityPlaceOfServiceError({
        modality,
        placeOfService,
      });
      if (sessionError) {
        setError(sessionError);
        return;
      }

      if (useJotmeet && modality !== 'video') {
        setError('JotMeet can only be used with video appointments.');
        return;
      }

      setIsSubmitting(true);
      setError(null);

      if (mode === 'create') {
        await handleCreateSubmission(startDateTime, duration);
      } else {
        await handleEditSubmission(startDateTime, duration);
      }
    } catch (err) {
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} appointment:`, err);
      setError(
        `Failed to ${mode === 'create' ? 'create' : 'update'} appointment. Please try again.`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateSubmission = async (startDateTime: Date, duration: number) => {
    if (!selectedPatient?.patient_id || !selectedAppointmentType?.encounter_type_id) {
      setError('Missing required fields');
      return;
    }

    // Convert forms to API format
    const formRules =
      selectedForms
        ?.map(form => ({
          form_definition_id: form.formId || '',
          offset: form.offset || '',
          title: form.name,
          id: form.ruleId,
        }))
        .filter(rule => rule.form_definition_id) || [];

    // Convert reminders to API format
    const reminderRules =
      selectedReminders?.map(reminder => ({
        reminder_template_id: reminder.name,
        offset: reminder.offset || '',
        title: reminder.name,
        id: reminder.ruleId,
      })) || [];

    // Create base appointment data
    const resolvedTelehealth =
      modality !== undefined ? getTelehealthForModality(modality) : telehealth;
    const cleanedCptCodes = cptCodes !== undefined ? normalizeCptCodes(cptCodes) : undefined;
    const sessionBilling =
      placeOfService !== undefined || cleanedCptCodes !== undefined
        ? buildSessionBillingPayload({
            placeOfService,
            cptCodes: cleanedCptCodes,
          })
        : undefined;
    const appointmentData = {
      patient_id: selectedPatient.patient_id,
      encounter_type_id: selectedAppointmentType.encounter_type_id,
      // Use template_id from the selected appointment type first, fall back to legacy templateId prop
      // The templateId prop is templatesList?.[0]?.template_id from parent components - a temporary fallback
      template_id: selectedAppointmentType.template_id || templateId || null,
      start_time: startDateTime.toISOString(),
      duration: duration,
      ...(providerId && { provider_id: providerId }),
      ...(resolvedTelehealth !== undefined && { telehealth: resolvedTelehealth }),
      ...(modality !== undefined && { modality: modality ?? null }),
      ...(sessionBilling !== undefined && { session_billing: sessionBilling }),
      ...(useJotmeet !== undefined && { use_jotmeet: Boolean(useJotmeet && modality === 'video') }),
      ...(formRules.length > 0 && { form_rules: formRules }),
      ...(reminderRules.length > 0 && { reminder_rules: reminderRules }),
      ...(overridesTypeDefaults && { override_defaults: true }),
      ...(notes ? { notes } : {}),
    };

    let createdAppointmentOrSeries: AppointmentResponse | SeriesResponse;

    // Generate recurrence rule if applicable
    const recurrenceData = generateRecurrenceData();
    if (recurrenceData) {
      // Create series data with our recurrence rule
      // For series, we need to use default_form_rules and default_reminder_rules
      const { form_rules, reminder_rules, override_defaults, provider_id, ...baseData } =
        appointmentData;
      const seriesData = {
        ...baseData,
        rrule: recurrenceData.rrule,
        end_date: recurrenceData.endDate,
        ...(provider_id && { provider_id }),
        ...(formRules.length > 0 && { default_form_rules: formRules }),
        ...(reminderRules.length > 0 && {
          default_reminder_rules: reminderRules,
        }),
        ...(labelIds.length > 0 && { default_label_ids: labelIds }),
        ...(overridesTypeDefaults && { override_defaults: true }),
      };

      createdAppointmentOrSeries = await createAppointmentSeries({
        appointmentSeriesCreate: seriesData,
      }).unwrap();
    } else {
      // Create single appointment
      const createdAppointment = await createAppointment({
        appointmentCreate: appointmentData,
      }).unwrap();
      createdAppointmentOrSeries = createdAppointment;

      // AppointmentCreate doesn't accept labels and the backend auto-applies the
      // type's defaults, so only PATCH when the user adjusted the selection away
      // from those defaults. Non-fatal: the appointment already exists.
      const typeDefaults = selectedAppointmentType.default_label_ids ?? [];
      // Set membership, not order: the label set is unordered, so compare by
      // membership rather than a sorted-string equality (which is brittle to any
      // id-format normalization).
      const defaultSet = new Set(typeDefaults);
      const labelsChanged =
        labelIds.length !== typeDefaults.length || labelIds.some(id => !defaultSet.has(id));
      if (labelsChanged && createdAppointment.appointment_id) {
        try {
          await updateAppointment({
            appointmentId: createdAppointment.appointment_id,
            appointmentUpdate: { label_ids: labelIds },
          }).unwrap();
        } catch (labelErr) {
          console.error('Failed to apply appointment labels:', labelErr);
          enqueueSnackbar(
            'Appointment created, but its labels could not be applied. Please re-apply them.',
            { variant: 'warning' },
          );
        }
      }
    }

    // Show success notification
    const notificationData = formatAppointmentCreatedNotification(createdAppointmentOrSeries);
    enqueueSnackbar('New Appointment Created!', {
      variant: 'success',
      data: notificationData,
    });

    // Call the callback if provided
    if (onAppointmentCreated) {
      onAppointmentCreated(createdAppointmentOrSeries);
    }
  };

  const handleEditSubmission = async (startDateTime: Date, duration: number) => {
    if (!appointment?.appointment_id || !onAppointmentUpdate) {
      setError('Missing appointment data or update handler');
      return;
    }

    // Prepare update data using the new model format
    const updateData: AppointmentUpdateWithSessionBilling = {
      start_time: startDateTime.toISOString(),
      duration: duration,
    };

    // Include patient_id if a patient is selected
    if (selectedPatient) {
      updateData.patient_id = selectedPatient.patient_id;
    }

    // Send only the encounter_type_id on updates; the backend derives the type name/template.
    if (selectedAppointmentType?.encounter_type_id) {
      updateData.encounter_type_id = selectedAppointmentType.encounter_type_id;
    }

    // Convert forms to API format
    const formRules =
      selectedForms
        ?.map(form => ({
          form_definition_id: form.formId || '',
          offset: form.offset || '',
          title: form.name,
          id: form.ruleId,
        }))
        .filter(rule => rule.form_definition_id) || [];

    // Convert reminders to API format
    const reminderRules =
      selectedReminders?.map(reminder => ({
        reminder_template_id: reminder.name,
        offset: reminder.offset || '',
        title: reminder.name,
        id: reminder.ruleId,
      })) || [];

    // Include form and reminder rules if present
    if (formRules.length > 0) {
      updateData.form_rules = formRules;
    }
    if (reminderRules.length > 0) {
      updateData.reminder_rules = reminderRules;
    }

    // Include telehealth if provided
    if (modality !== undefined) {
      updateData.modality = modality ?? null;
      updateData.telehealth = getTelehealthForModality(modality);
    } else if (telehealth !== undefined) {
      updateData.telehealth = telehealth;
    }

    if (placeOfService !== undefined || cptCodes !== undefined) {
      updateData.session_billing = buildSessionBillingPayload({
        placeOfService:
          placeOfService !== undefined
            ? placeOfService
            : getSessionBillingPlaceOfService(appointment),
        cptCodes: cptCodes !== undefined ? cptCodes : getSessionBillingCptCodes(appointment),
      });
    }

    if (useJotmeet !== undefined) {
      updateData.use_jotmeet = Boolean(useJotmeet && modality === 'video');
    }

    if (overridesTypeDefaults) {
      updateData.override_defaults = true;
    }

    // Include notes — send empty string to clear (backend ignores null on PATCH)
    if (notes !== undefined) {
      updateData.notes = notes || '';
    }

    // Labels: send the full desired set (backend replaces; empty array clears all)
    updateData.label_ids = labelIds;

    // Generate recurrence rule if applicable
    const recurrenceData = generateRecurrenceData();
    if (recurrenceData) {
      updateData.rrule = recurrenceData.rrule;

      if (recurrenceData.endDate) {
        updateData.series_end_date = recurrenceData.endDate;
      }

      // If this appointment wasn't already part of a series, flag it to create a new series
      if (!appointment.series) {
        updateData.create_series = true;
      }
    }

    // Always use the onAppointmentUpdate handler with optional seriesId
    await onAppointmentUpdate(
      appointment.appointment_id,
      updateData,
      appointment.series?.series_id,
    );
  };

  return {
    isSubmitting,
    error,
    submit,
    setError,
  };
};
